import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { NAV_GROUPS } from "../../lib/nav.js";
import { SITE } from "../../lib/site.js";
import { track } from "../../lib/analytics.js";
import MobileDrawer from "../MobileDrawer/MobileDrawer.jsx";
import Picture, { CARD } from "../Picture/Picture.jsx";
import styles from "./Header.module.css";
import { LOGO_VERSION, withVersion } from "../../lib/assetVersions.js";

function NavFeature({ feature }) {
  return (
    <Link to={feature.href} className={styles.feature}>
      {feature.image ? (
        <Picture
          src={feature.image}
          alt=""
          {...CARD}
          sizes="22rem"
          className={styles.featureImage}
        />
      ) : (
        <span className={styles.featureLogos}>
          {feature.logos.map((logo) => (
            <picture key={logo.src}>
              <source
                type="image/webp"
                srcSet={withVersion(`${logo.src}-mono.webp`, LOGO_VERSION)}
              />
              <img
                src={withVersion(`${logo.src}-mono.png`, LOGO_VERSION)}
                alt={logo.name}
                height="48"
                loading="lazy"
              />
            </picture>
          ))}
        </span>
      )}
      <span className={styles.featureEyebrow}>{feature.eyebrow}</span>
      <span className={styles.featureTitle}>{feature.title}</span>
      <span className={styles.featureCta}>
        {feature.cta}
        <span className={styles.featureArrow} aria-hidden="true" />
      </span>
    </Link>
  );
}

export default function Header() {
  const [openGroup, setOpenGroup] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  const navRef = useRef(null);
  const stickySentinelRef = useRef(null);
  const closeTimer = useRef(null);
  const location = useLocation();

  // Hover intent: leaving the header only closes the menu after a short delay, and any
  // re-entry (the panel, the gap bridge, another tab) cancels it — so a pointer that
  // overshoots the gap on its way into the panel doesn't lose the menu.
  const cancelClose = () => clearTimeout(closeTimer.current);
  const openMenu = (i) => {
    cancelClose();
    setOpenGroup(i);
  };
  const closeMenuSoon = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenGroup(null), 250);
  };
  const closeMenuNow = () => {
    cancelClose();
    setOpenGroup(null);
  };
  useEffect(() => cancelClose, []);

  // The header is `position: sticky`, which has no CSS hook for "currently stuck" —
  // a zero-height sentinel just above it in flow does: once scrolling carries the
  // sentinel out of view, the header must be pinned to its sticky offset instead of
  // sitting at its normal in-flow position, so we nudge it up slightly at that point.
  useEffect(() => {
    const el = stickySentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), {
      threshold: 0,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Close any open menu/drawer when the route changes. Derived during render
  // (rather than in an effect) so it happens before paint, with no extra render.
  const [lastPathname, setLastPathname] = useState(location.pathname);
  if (location.pathname !== lastPathname) {
    setLastPathname(location.pathname);
    setOpenGroup(null);
    setDrawerOpen(false);
  }

  useEffect(() => {
    if (openGroup === null) return undefined;
    // Attached imperatively (not as a JSX handler on the menu div, which is a
    // static element) so Escape closes the menu and arrow keys move between
    // its links while it's open.
    const onKey = (event) => {
      if (event.key === "Escape") {
        setOpenGroup(null);
        navRef.current?.querySelectorAll("button")[openGroup]?.focus();
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        const panel = navRef.current?.querySelector(`#nav-menu-${openGroup}`);
        const links = panel ? [...panel.querySelectorAll("a")] : [];
        if (links.length === 0) return;
        const index = links.indexOf(document.activeElement);
        const next =
          event.key === "ArrowDown"
            ? links[index + 1] || links[0]
            : links[index - 1] || links.at(-1);
        next?.focus();
        event.preventDefault();
      }
    };
    const onPointer = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) setOpenGroup(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [openGroup]);

  return (
    <>
      <div ref={stickySentinelRef} className={styles.stickySentinel} aria-hidden="true" />
      <header
        className={`${styles.header} ${isStuck ? styles.stuck : ""}`}
        // Leaving the whole header — not just the tab — is what closes the menu: the panel
        // spans the header's width below it, so the pointer crosses the header's own
        // padding and the gap under it on the way from a tab to its panel.
        onMouseEnter={cancelClose}
        onMouseLeave={closeMenuSoon}
      >
        <div className={styles.bar}>
          <Link
            to="/"
            className={styles.logo}
            aria-label="RDV Systems home"
            onMouseEnter={closeMenuNow}
          >
            <img src="/images/site/logo-white.png" alt="" width="315" height="48" />
          </Link>

          <nav className={styles.nav} aria-label="Main" ref={navRef}>
            <ul className={styles.groups}>
              {NAV_GROUPS.map((group, i) => (
                <li key={group.label} onMouseEnter={() => openMenu(i)}>
                  <button
                    type="button"
                    className={styles.groupButton}
                    aria-expanded={openGroup === i}
                    aria-controls={`nav-menu-${i}`}
                    onClick={() => setOpenGroup(openGroup === i ? null : i)}
                  >
                    {group.label}
                    <span className={styles.chevron} aria-hidden="true" />
                  </button>
                  <div id={`nav-menu-${i}`} className={styles.menu} hidden={openGroup !== i}>
                    <div className={styles.menuLinks}>
                      <p className={styles.menuEyebrow}>{group.label}</p>
                      <ul className={styles.menuList}>
                        {group.items.map((item) => (
                          <li key={item.href}>
                            <NavLink to={item.href} className={styles.menuLink} end>
                              <span className={styles.menuLabel}>{item.label}</span>
                              <span className={styles.menuBlurb}>{item.blurb}</span>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {group.feature ? <NavFeature feature={group.feature} /> : null}
                  </div>
                </li>
              ))}
            </ul>
          </nav>

          <div className={styles.actions} onMouseEnter={closeMenuNow}>
            <a
              className={styles.login}
              href={SITE.loginUrl}
              onClick={() => track("outbound_login")}
            >
              <span className={styles.loginIcon} aria-hidden="true" />
              Sign In
            </a>
            {/* The one action the site is asking for, kept in the bar at every width. */}
            <Link to="/demo" className={styles.demo}>
              Request a demo
            </Link>
            <button
              type="button"
              className={styles.burger}
              aria-expanded={drawerOpen}
              aria-controls="mobile-drawer"
              onClick={() => setDrawerOpen(true)}
            >
              <span className="sr-only">Open menu</span>
              <span className={styles.burgerIcon} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>
      {/* Outside <header>, not inside it: the header's backdrop-filter makes it the containing
          block for position: fixed descendants, which shrank the full-screen drawer to the
          64px bar (2026-09-27, when the blur came back in production). */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}
